import * as THREE from "three";

/**
 * Six-degree-of-freedom flight.
 *
 * Spark's built-in controller locomotion walks you around a horizontal plane,
 * which is the wrong model for a generated world. Captures have no floor you are
 * obliged to respect, the interesting geometry is often above head height, and
 * being pinned to one altitude makes a world feel like a diorama you are
 * circling rather than a space you are in. So: full flight.
 *
 *   left stick    strafe / forward-back, relative to where you are looking
 *   right stick   turn (X) and rise / descend (Y)
 *
 * Turning rotates about the head rather than the rig origin. Rotating about the
 * rig makes the whole world swing around you when you are not standing at the
 * rig's centre, which is instantly nauseating.
 */

const DEAD_ZONE = 0.12;
const BASE_SPEED = 2.4;        // metres per second at full stick
const TURN_SPEED = 2.0;        // radians per second at full stick
const LIFT_SPEED = 1.8;        // metres per second
/** Squeeze acts as a sprint, matching the convention Spark used. */
const BOOST = 3.0;
const SLOW = 0.3;

function applyDeadZone(value: number): number {
  if (Math.abs(value) < DEAD_ZONE) {
    return 0;
  }
  // Rescale so motion starts from zero at the dead-zone edge rather than
  // jumping straight to DEAD_ZONE worth of speed.
  const sign = Math.sign(value);
  return sign * ((Math.abs(value) - DEAD_ZONE) / (1 - DEAD_ZONE));
}

export interface FlightInput {
  readonly strafe: number;
  readonly forward: number;
  readonly turn: number;
  readonly lift: number;
  readonly boost: boolean;
  readonly slow: boolean;
}

export function readFlightInput(session: XRSession | null): FlightInput {
  let strafe = 0;
  let forward = 0;
  let turn = 0;
  let lift = 0;
  let boost = false;
  let slow = false;

  if (session === null) {
    return { strafe, forward, turn, lift, boost, slow };
  }

  for (const source of session.inputSources) {
    const gamepad = source.gamepad;
    if (gamepad === undefined || gamepad === null) {
      continue;
    }
    // Quest controllers report the thumbstick on axes 2/3; axes 0/1 are the
    // trackpad slot and stay at zero. Fall back for other runtimes.
    const axes = gamepad.axes;
    const x = applyDeadZone(axes[2] ?? axes[0] ?? 0);
    const y = applyDeadZone(axes[3] ?? axes[1] ?? 0);

    // Triggers are an analogue vertical axis as well as the right stick:
    // left trigger descends, right trigger climbs. Reaching for a trigger to
    // change height is the more natural instinct than remembering which stick
    // axis owns altitude, so both are supported.
    const trigger = gamepad.buttons[0]?.value ?? 0;

    if (source.handedness === "left") {
      strafe += x;
      forward += y;
      lift -= trigger;
      boost = boost || (gamepad.buttons[1]?.pressed ?? false);
    } else if (source.handedness === "right") {
      turn += x;
      // Stick forward is negative; pushing forward should rise.
      lift -= y;
      lift += trigger;
      slow = slow || (gamepad.buttons[1]?.pressed ?? false);
    }
  }

  return {
    strafe,
    forward,
    turn,
    // Both hands can drive altitude; clamp so pressing both does not double it.
    lift: THREE.MathUtils.clamp(lift, -1, 1),
    boost,
    slow
  };
}

const forwardVector = new THREE.Vector3();
const rightVector = new THREE.Vector3();
const motion = new THREE.Vector3();
const headWorld = new THREE.Vector3();
const headLocal = new THREE.Vector3();
const turnQuat = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

/**
 * Applies flight to the camera rig.
 *
 * `head` must be the XR camera, read via matrixWorld — in an immersive session
 * three never writes its position/quaternion, so the usual getters return the
 * rig's transform instead of the head's.
 */
export function applyFlight(
  rig: THREE.Object3D,
  // Any Object3D whose matrixWorld holds the head pose. IWSDK's XROrigin.head
  // is a Group, not a Camera, and only matrixWorld is read here.
  head: THREE.Object3D,
  input: FlightInput,
  deltaSeconds: number
): void {
  const multiplier = input.boost ? BOOST : input.slow ? SLOW : 1;

  if (input.turn !== 0) {
    const angle = -input.turn * TURN_SPEED * deltaSeconds;
    turnQuat.setFromAxisAngle(UP, angle);

    // Rotate the rig about the head's ground position so the world turns
    // around the viewer rather than swinging them through an arc.
    headWorld.setFromMatrixPosition(head.matrixWorld);
    headLocal.copy(headWorld);
    rig.parent?.worldToLocal(headLocal);

    rig.position.sub(headLocal).applyQuaternion(turnQuat).add(headLocal);
    rig.quaternion.premultiply(turnQuat);
  }

  if (input.strafe !== 0 || input.forward !== 0 || input.lift !== 0) {
    // Heading comes from where you are looking, flattened — looking up should
    // not make "forward" fly you into the sky. Altitude is the lift axis's job.
    const m = head.matrixWorld.elements;
    forwardVector.set(-m[8]!, 0, -m[10]!);
    if (forwardVector.lengthSq() < 1e-6) {
      forwardVector.set(0, 0, -1);
    }
    forwardVector.normalize();
    // forward x up already points to the viewer's right when looking down -Z.
    // A negate() here was compensating for a sign in the previous hand-rolled
    // input layer and inverted strafe once IWSDK started supplying the stick.
    rightVector.crossVectors(forwardVector, UP).normalize();

    motion.set(0, 0, 0);
    motion.addScaledVector(forwardVector, -input.forward);
    motion.addScaledVector(rightVector, input.strafe);
    motion.y += input.lift * (LIFT_SPEED / BASE_SPEED);
    motion.multiplyScalar(BASE_SPEED * multiplier * deltaSeconds);

    rig.position.add(motion);
  }
}
